import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import type { User } from '@/types/auth'
import { authService } from '@/services/authService'
import { cancelSessionExpiry, expireSession, scheduleSessionExpiry, tokenExpiryTime } from '@/utils/sessionExpiry'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isNativeMobile = Capacitor.isNativePlatform()
  let refreshInProgress: Promise<void> | null = null

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const userRole = computed(() => user.value?.role || null)
  const userName = computed(() => user.value ? `${user.value.firstName} ${user.value.lastName}` : '')

  // Actions
  function setTokens(access: string, refresh: string) {
    accessToken.value = access
    refreshToken.value = refresh
    scheduleSessionExpiry(access, () => { void refreshSession() }, 60_000)
  }

  const setUser = (userData: User | any) => {
    const normalizedRole = userData?.role || userData?.roles?.[0] || userData?.Roles?.[0] || null
    user.value = {
      ...userData,
      role: normalizedRole
    } as User
  }

  async function refreshSession(): Promise<void> {
    if (!refreshToken.value) {
      expireSession()
      return
    }
    if (refreshInProgress) return refreshInProgress

    refreshInProgress = (async () => {
      try {
        const response = await authService.refreshToken(refreshToken.value || '')
        if (!response.accessToken || !response.refreshToken) throw new Error('Session refresh failed')
        setTokens(response.accessToken, response.refreshToken)
        if (response.user) setUser(response.user)
      } catch (err: any) {
        const status = err?.response?.status
        if (status === 401 || status === 400) {
          expireSession()
        }
        throw err
      } finally {
        refreshInProgress = null
      }
    })()
    return refreshInProgress
  }

  const clearAuth = () => {
    cancelSessionExpiry()
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    error.value = null
  }

  const requestOtp = async (identifier: string, type: 'email' = 'email') => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.requestOtp(identifier, 'email')
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to send OTP'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const verifyOtp = async (identifier: string, otp: string, userData?: any) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.verifyOtp(identifier, otp, userData)
      if (response.accessToken && response.refreshToken) {
        setTokens(response.accessToken, response.refreshToken)
      }
      setUser(response.user)
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'OTP verification failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const refreshAccessToken = async () => {
    await refreshSession()
  }

  const loginWithPassword = async (email: string, password: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.loginWithPassword(email, password)
      if (response.accessToken && response.refreshToken) {
        setTokens(response.accessToken, response.refreshToken)
      }
      setUser(response.user)
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      clearAuth()
    }
  }

  const getCurrentUser = async () => {
    const userData = await authService.getCurrentUser()
    setUser(userData)
    return userData
  }

  const shouldRefreshSoon = (token: string | null) => {
    const expiresAt = token ? tokenExpiryTime(token) : null
    return !expiresAt || expiresAt <= Date.now() + 60_000
  }

  const initializeAuth = async () => {
    if (!accessToken.value || !user.value) return

    try {
      if (refreshToken.value && shouldRefreshSoon(accessToken.value)) {
        await refreshSession()
      } else {
        scheduleSessionExpiry(accessToken.value, () => { void refreshSession() }, 60_000)
      }
    } catch (err) {
      console.warn('[Auth] Session refresh deferred until the API is reachable')
      if (!accessToken.value) return
    }

    // Keep persisted user visible immediately; refresh profile in background.
    void getCurrentUser().catch(() => {
      console.warn('[Auth] Kept the saved session while the profile refresh failed')
    })
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || !accessToken.value || !refreshToken.value) return
      if (shouldRefreshSoon(accessToken.value)) void refreshSession()
    })
  }

  if (isNativeMobile) {
    void App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive || !accessToken.value || !refreshToken.value) return
      if (shouldRefreshSoon(accessToken.value)) void refreshSession()
    })
  }

  // Bypass authentication for test emails - now calls backend
  const bypassAuth = async (identifier: string) => {
    try {
      // Call the backend quick-login endpoint
      const response = await authService.quickLogin(identifier)
      
      if (response.accessToken) {
        setTokens(response.accessToken, response.refreshToken)
        // Map roles array to single role string if needed
        const userData = {
          ...response.user,
          role: response.user.role || (response.user.roles?.[0] as any) || 'Admin'
        }
        setUser(userData)
        
        return {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          user: userData
        }
      } else {
        throw new Error(response.message || 'Quick login failed')
      }
    } catch (error: any) {
      console.error('[Auth Store] Quick login failed:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Quick login failed'
      throw new Error(errorMessage)
    }
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    userRole,
    userName,

    // Actions
    setTokens,
    setUser,
    clearAuth,
    requestOtp,
    verifyOtp,
    loginWithPassword,
    refreshAccessToken,
    refreshSession,
    logout,
    getCurrentUser,
    initializeAuth,
    bypassAuth
  }
}, {
  persist: {
    key: 'mastermind-auth',
    storage: localStorage,
    paths: ['user', 'accessToken', 'refreshToken']
  }
})
